/**
 * TanStack Query client + provider.
 *
 * Defaults tuned for compliance data:
 *   - `staleTime: 30s` — list/detail views are not real-time; treat data
 *     as fresh for half a minute so navigating between screens doesn't
 *     re-fetch immediately.
 *   - `gcTime: 5m` — keep unmounted queries in cache for fast back-nav.
 *   - `retry: 1` for queries, `0` for mutations — never retry mutations
 *     blindly (idempotency keys make manual retry safe; auto-retry can
 *     accidentally fire a second mutation if the first's response was
 *     just slow).
 *   - `refetchOnWindowFocus: false` — avoids flicker when the user
 *     alt-tabs back; we'll add manual refetch buttons where it matters.
 *   - On `ApiError` with code CONTRACT_VIOLATION we never retry — that's
 *     a deterministic bug, not a transient failure.
 */

import { ApiError, isApiError } from "@compliance/domain";
import { QueryClient } from "@tanstack/react-query";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (failureCount >= 1) return false;
          if (isApiError(error)) {
            // Don't retry 4xx — they won't change.
            if (error.httpStatus >= 400 && error.httpStatus < 500) return false;
            // Don't retry contract violations — deterministic bug.
            if (error.code === "CONTRACT_VIOLATION") return false;
          }
          return true;
        },
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export { ApiError };
